import {
  APIAttachment,
  APIDuplicateAttachmentGroup,
  ArdoqId,
} from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { parseToTimestamp } from '@ardoq/date-time';

export type DuplicateCopyStatus = 'keep' | 'inUse' | 'remove';

export type DuplicateCopy = {
  _id: ArdoqId;
  created: string;
  status: DuplicateCopyStatus;
};

export type DuplicateGroup = {
  filename: string;
  size: number;
  attachments: DuplicateCopy[];
};

type UnresolvedCopy = {
  _id: ArdoqId;
  created: string;
  isInUse: boolean;
};

const byCreated = (a: UnresolvedCopy, b: UnresolvedCopy): number =>
  parseToTimestamp(a.created) - parseToTimestamp(b.created);

const toStatus = (
  copy: UnresolvedCopy,
  keptUnusedId: ArdoqId | null
): DuplicateCopyStatus => {
  if (copy.isInUse) {
    return 'inUse';
  }
  return copy._id === keptUnusedId ? 'keep' : 'remove';
};

const toKeptUnusedId = (copies: UnresolvedCopy[]): ArdoqId | null => {
  if (copies.some(copy => copy.isInUse)) {
    return null;
  }
  return copies[0]?._id ?? null;
};

const toDuplicateGroup = (
  group: APIDuplicateAttachmentGroup,
  attachmentsById: Map<ArdoqId, APIAttachment>
): DuplicateGroup | null => {
  const known = group.attachments
    .map(({ _id, usages }) => {
      const attachment = attachmentsById.get(_id);
      return (
        attachment && {
          attachment,
          copy: {
            _id,
            created: attachment.created,
            isInUse: usages.length > 0,
          },
        }
      );
    })
    .filter(isTruthy);
  if (known.length < 2) {
    return null;
  }
  const copies = known.map(({ copy }) => copy).sort(byCreated);
  const keptUnusedId = toKeptUnusedId(copies);
  return {
    filename: group.filename,
    size: known[0].attachment.size,
    attachments: copies.map(copy => ({
      _id: copy._id,
      created: copy.created,
      status: toStatus(copy, keptUnusedId),
    })),
  };
};

export const toDuplicateGroups = (
  groups: APIDuplicateAttachmentGroup[],
  attachments: APIAttachment[]
): DuplicateGroup[] => {
  const attachmentsById = new Map(
    attachments.map(attachment => [attachment._id, attachment])
  );
  return groups
    .map(group => toDuplicateGroup(group, attachmentsById))
    .filter(isTruthy);
};
